/**
 * The graph footer (D369) — the replay transport, the scrubber and the view's
 * switches, in one strip along the field's bottom edge.
 *
 * The scrubber draws one tick per step of the exploration, left to right in the
 * order they landed, tick HEIGHT encoding what that step drew, so the shape of
 * the run's spending is readable at a glance: a refuted branch that took three
 * dear passes is tall bars, a run of cheap gaps is stubs. The steps stand under
 * the name of the pass that produced them. Sweeping it magnifies the ticks under
 * the cursor and raises a card with that step's figures above the footer; a
 * press seeks the replay there and a drag scrubs. The card's title and clamped
 * claim are a press too — the node's full reading, in the thread
 * (`inspectPress.ts`). A hairline marks the step that took the exploration past
 * the cap typed in the composer.
 *
 * The keyboard reaches the same gestures (`railKeys.ts` says why this is a
 * listbox): the rail is one tab stop, the arrows move a highlight that raises
 * the card where a sweep would, Enter seeks, and Shift+B branches from the
 * highlighted step through the card's own press. Each step is named out loud —
 * its number, its verb, its claim, its figure — because the card is not.
 *
 * Everything here reads `core/ledger.ts` — one arithmetic for every scrubbed
 * figure — and the LIVE figure beside it reads the per-model ledger, the record
 * a user can check against a provider's dashboard. See the ledger's header for
 * why those are two scopes and where the seam is.
 *
 * The magnification and the card's position are written per animation frame,
 * straight to style, and never through React state: a React pass per pointer
 * move is how a 60fps sweep becomes a 12fps one. React owns the resting state —
 * tick colors, heights, the card's CONTENT — via CSS custom properties; the
 * frame loop only overrides, and clears its overrides when the cursor settles.
 */

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type {
  CSSProperties,
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { useStore } from '../core/store'
import { capIndex, parseCap, stepLedger, type Step } from '../core/ledger'
import { estimateSpendUsd, fmtUsd } from '../core/models'
import {
  channelOf,
  kindLabelOf,
  OP_LABEL,
  type Op,
  type OpKind,
  type ReasonNode,
  type Session,
} from '../core/types'
import { COPY_REPLAY_MS, replayPace } from '../core/tempo'
import { CHANNEL_WORD, STATUS_WORD } from '../core/vocabulary'
import { Icon } from './components/Icon'
import { prunePress } from './Coach'
import { takeInspect } from './inspectPress'
import { railKey, seekSaid, stepName } from './railKeys'
import { useReadFirst } from './SharedCopyBanner'
import { ViewToggles } from './ViewToggles'

// The channels are named by `CHANNEL_WORD` in core/vocabulary.ts — the bottom
// key's own words, which the rail must not rename: two vocabularies for one hue is
// how a color stops meaning anything.

/** What landed a step, for the transport's step title and the card. The verbs read from
 *  `OP_LABEL` so the rail and the inspector cannot call one move two things.
 *  `origin: 'seed'` on a NON-root node means it was present at load — the
 *  demo's pre-authored branches — and "Seed" over those would claim eighteen
 *  seeds, so they read as authored instead. */
const originWord = (o: OpKind | 'seed' | 'manual', root: boolean): string =>
  o === 'seed' ? (root ? 'Seed' : 'Authored') : o === 'manual' ? 'Yours' : OP_LABEL[o]

/** Who made a step: you, a model you asked, or the autopilot. A pass the
 *  autopilot chose is told from one you pressed by the run's own move list —
 *  `RunMove.opId` is the join, and `Op` carries no run field on purpose (see
 *  `RunMove`). The demo's pre-authored branches were made by none of the three,
 *  and calling them the model's would credit a model that never saw them. */
const authorOf = (
  origin: OpKind | 'seed' | 'manual',
  root: boolean,
  byAutopilot: boolean,
): 'You' | 'Model' | 'Autopilot' | 'Demo' =>
  origin === 'manual' || (origin === 'seed' && root)
    ? 'You'
    : origin === 'seed'
      ? 'Demo'
      : byAutopilot
        ? 'Autopilot'
        : 'Model'

const fmtClock = (ms: number): string => {
  const s = Math.max(0, Math.round(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Cosine falloff — the magnification's whole shape. The lens is four steps
 *  wide at the least, and never narrower than `LENS_PX` on screen: a long run
 *  in a narrow footer puts a step under a pixel, and four of those is a lens
 *  nobody can see. */
const RADIUS = 4
const LENS_PX = 18
/** How tall the tick under the cursor stands, at the lens's peak. */
const MAG_H = 20

/** The card's resting width — `.steprail-card`'s own 344, restated because
 *  this file decides when it gives way. It rises above the footer, centred on
 *  the tick it describes and held inside the window, so it yields only to a
 *  window narrower than itself — narrower when the window leaves less, and
 *  below CARD_MIN not raised at all, where the rows' own `title` still names
 *  the step and its figure. */
const CARD_W = 344
/** Below this the head row and the branch press shear apart — a card that
 *  cannot say its figures legibly is chrome pretending, so none rises. */
const CARD_MIN = 168
/** Daylight kept from the window's edges — the popovers' own 10. */
const CARD_CLEAR = 10

const rise = (d: number, radius: number): number =>
  d >= radius ? 0 : 0.5 * (1 + Math.cos((Math.PI * d) / radius))

/**
 * Where the scrubber's passes begin, after the first: each run of consecutive
 * steps one pass produced, and the steps an autopilot run produced as one,
 * however many passes it took. A step no recorded op produced — the seed, the
 * demo's authored branches, a claim of your own, a pass the op log has since
 * dropped — belongs with its neighbours that landed the same way. Unnamed, at
 * the owner's word (D373): a hairline marks where one begins, as a chapter mark
 * does on a player's seek bar, and the step card says what each step is.
 */
export function passStarts(session: Session, steps: readonly Step[]): number[] {
  const runOf = new Map<string, number>()
  session.runs.forEach((r, ri) => r.moves.forEach((m) => runOf.set(m.opId, ri)))
  // The first op that produced a node, the ledger's own rule (`stepLedger`).
  const opOf = new Map<string, Op>()
  for (const op of session.ops) for (const id of op.produced) if (!opOf.has(id)) opOf.set(id, op)
  const out: number[] = []
  let last = ''
  steps.forEach((st, i) => {
    const op = opOf.get(st.nodeId)
    const run = op ? runOf.get(op.id) : undefined
    const key = run !== undefined ? `run:${run}` : op ? `op:${op.id}` : `origin:${st.origin}`
    if (key !== last && i > 0) out.push(i)
    last = key
  })
  return out
}

/* ── The rail ─────────────────────────────────────────────────────────── */

/**
 * The scrubber, along the graph's footer (D369): one tick per step, left to
 * right in the order the steps landed, tick HEIGHT what the step drew, under
 * the name of the pass that produced it. The playhead stands on the step the
 * replay has reached — the last one, live — with its knob on the footer's top
 * edge. A press seeks, a drag scrubs, and a sweep magnifies the ticks under
 * the cursor and raises the step's card above the footer.
 *
 * It was a vertical spine on the field's left edge, which had to give way to
 * the session column and hang from its floor; along the footer it has a strip
 * of its own and gives way to nothing.
 */
export function StepRail() {
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const playhead = useStore((s) => s.playhead)
  const runLive = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  const runCeiling = useStore((s) => s.runCeiling)
  const setPlayhead = useStore((s) => s.setPlayhead)
  const setPlaying = useStore((s) => s.setPlaying)

  const ledger = useMemo(() => stepLedger(session), [session, rev])
  const cap = parseCap(runCeiling)
  const capIx = capIndex(ledger, cap)
  /** The nodes the autopilot's own passes produced — the card's "Autopilot". */
  const autoNodes = useMemo(() => {
    const ops = new Set(session.runs.flatMap((r) => r.moves.map((m) => m.opId)))
    const out = new Set<string>()
    for (const op of session.ops) if (ops.has(op.id)) for (const id of op.produced) out.add(id)
    return out
  }, [session, rev])
  const starts = useMemo(() => passStarts(session, ledger.steps), [session, ledger])

  const total = session.order.length
  const at = playhead ?? total

  /** Sweep state, off-React. `pos` eases toward the cursor, `strength` toward
   *  hover/rest; both are read by one frame loop that writes styles directly.
   *  `cardW` is the width the window leaves the card: 0 for the resting 344,
   *  a positive cap when the window is narrow, -1 when even CARD_MIN cannot
   *  stand and no card rises. `left` is the rail's own left edge on screen,
   *  which the card's placement is held inside the window by. */
  const sweep = useRef({ pos: 0, target: 0, strength: 0, want: 0, raf: 0, cardW: 0, left: 0 })
  /**
   * The rail's element, as STATE rather than a ref, because the sweep's
   * listeners are attached in an effect and the rail mounts and unmounts under
   * it (under two steps there is no rail). A ref never re-runs that effect, and
   * a rail whose listeners were attached to the element before it is painted
   * but deaf: rows still seek on press, while the sweep and its card stay dead.
   */
  const [railEl, setRailEl] = useState<HTMLDivElement | null>(null)
  /** The rail's width, measured: the ticks' width, the lens and which pass
   *  names fit all read it. */
  const [railW, setRailW] = useState(0)
  const barRefs = useRef<Array<HTMLDivElement | null>>([])
  const cardRef = useRef<HTMLDivElement | null>(null)
  /** The row the card is describing — React state, but only per whole row. */
  const [csIx, setCsIx] = useState<number | null>(null)
  /** The step the KEYBOARD is on, while the rail holds focus — the listbox's
   *  active descendant — and null while it does not. Its own state rather than
   *  `csIx`, which the pointer moves too: a sweep that crossed the rail under a
   *  focused list must not move where the next arrow starts from. */
  const [keyAt, setKeyAt] = useState<number | null>(null)
  /** Raise the card on a step, or let it fall — the keyboard's sweep. Installed
   *  by the frame loop's effect, which owns the sweep state it writes. */
  const aimRef = useRef<((ix: number | null) => void) | null>(null)
  /** The press that last seeked, so a drag that stays on one step seeks once. */
  const dragRef = useRef<number | null>(null)
  /** What the rail last said aloud: where Enter left the replay. */
  const [said, setSaid] = useState('')
  const listId = useId()

  useLayoutEffect(() => {
    if (!railEl) return
    const measure = () => setRailW(railEl.getBoundingClientRect().width)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(railEl)
    return () => ro.disconnect()
  }, [railEl])

  const pitch = total > 0 && railW > 0 ? railW / total : 0

  const seek = (i: number) => {
    setPlaying(false)
    setPlayhead(i + 1 >= total ? null : i + 1)
  }
  // The latest `seek`, for the listeners attached once per rail.
  const seekRef = useRef(seek)
  seekRef.current = seek

  useEffect(() => {
    const el = railEl
    if (!el || pitch === 0) return
    const sw = sweep.current
    const radius = Math.max(RADIUS, LENS_PX / pitch)

    const frame = () => {
      sw.pos += (sw.target - sw.pos) * 0.34
      sw.strength += (sw.want - sw.strength) * 0.16
      if (sw.want === 0 && sw.strength < 0.002) sw.strength = 0

      for (let i = 0; i < barRefs.current.length; i++) {
        const bar = barRefs.current[i]
        if (!bar) continue
        if (sw.strength === 0) {
          bar.style.removeProperty('height')
          bar.style.removeProperty('opacity')
          continue
        }
        const r = sw.strength * rise(Math.abs(i - sw.pos), radius)
        const rest = Number(bar.dataset.rest ?? 5)
        const base = Number(bar.dataset.base ?? 0.2)
        bar.style.height = `${rest + r * (Math.max(MAG_H, rest + 6) - rest)}px`
        bar.style.opacity = `${base + r * (1 - base)}`
      }

      const card = cardRef.current
      if (card) {
        // Interactive only while the pointer is actually engaged with the rail.
        // Keyed on `want` rather than `strength` so that leaving stops the
        // interception on the same frame: the card is 344px of canvas, and it
        // must not keep swallowing clicks through its fade out. And never while
        // the window has no room to raise it — an invisible card standing over
        // the field would take the canvas's own clicks.
        card.style.pointerEvents = sw.want && sw.cardW >= 0 ? 'auto' : 'none'
        if (sw.strength === 0 || sw.cardW < 0) {
          card.style.opacity = '0'
          // Visibility too, not only opacity: its presses are clickable, and an
          // invisible card must not intercept a click meant for the canvas.
          card.style.visibility = 'hidden'
        } else {
          // The width the window left, measured by `move`; '' hands the
          // resting 344 back to the stylesheet.
          card.style.width = sw.cardW ? `${sw.cardW}px` : ''
          const w = sw.cardW || CARD_W
          // Centred on the tick, and held inside the window: the footer runs to
          // the window's right edge, and a card centred on the last step would
          // hang half off it.
          const centre = (sw.pos + 0.5) * pitch
          const lo = CARD_CLEAR - sw.left
          const hi = window.innerWidth - CARD_CLEAR - w - sw.left
          const x = Math.round(Math.max(lo, Math.min(hi, centre - w / 2)))
          card.style.visibility = ''
          card.style.opacity = `${sw.strength}`
          card.style.transform = `translate(${x}px, ${(1 - sw.strength) * 6}px) scale(${0.972 + sw.strength * 0.028})`
        }
      }

      if (sw.strength > 0 || sw.want > 0) sw.raf = requestAnimationFrame(frame)
      else sw.raf = 0
    }

    const wake = () => {
      if (!sw.raf) sw.raf = requestAnimationFrame(frame)
    }
    // What the window leaves the card, re-asked on every move because both
    // ends of the answer drift under the pointer: the thread column slides the
    // footer with its 380ms fold, and the window resizes.
    const measure = (r: DOMRect) => {
      sw.left = r.left
      const room = Math.floor(window.innerWidth - 2 * CARD_CLEAR)
      sw.cardW = room >= CARD_W ? 0 : room >= CARD_MIN ? room : -1
    }
    const stepAt = (e: PointerEvent, r: DOMRect) =>
      Math.max(0, Math.min(barRefs.current.length - 1, (e.clientX - r.left) / pitch - 0.5))
    const onCard = (e: PointerEvent) => {
      // The card is a CHILD of this column and takes pointer events, so moving
      // onto it does not read as leaving the rail — that is what keeps it on
      // screen long enough to press. Its events still bubble here, though, and
      // sweeping over the card would retarget the very step it is describing
      // out from under the cursor. So the card's own area is inert to the sweep.
      const card = cardRef.current
      return card !== null && e.target instanceof Node && card.contains(e.target)
    }
    const move = (e: PointerEvent) => {
      if (onCard(e) && dragRef.current === null) return
      const r = el.getBoundingClientRect()
      measure(r)
      sw.target = stepAt(e, r)
      sw.want = 1
      const ix = Math.round(sw.target)
      setCsIx((cur) => (cur === ix ? cur : ix))
      // A drag scrubs: each step the press crosses is a seek, once.
      if (dragRef.current !== null && dragRef.current !== ix) {
        dragRef.current = ix
        seekRef.current(ix)
      }
      wake()
    }
    const down = (e: PointerEvent) => {
      if (e.button !== 0 || onCard(e)) return
      const ix = Math.round(stepAt(e, el.getBoundingClientRect()))
      dragRef.current = ix
      seekRef.current(ix)
      // A press on a focused list moves its highlight too, so the next arrow
      // starts from the step that was pressed.
      setKeyAt((k) => (k === null ? k : ix))
      // Held, so a drag that leaves the footer — up over the field — keeps
      // scrubbing until the press lets go.
      el.setPointerCapture(e.pointerId)
    }
    const up = (e: PointerEvent) => {
      dragRef.current = null
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
    }
    const leave = () => {
      if (dragRef.current !== null) return
      sw.want = 0
      wake()
    }
    // The keyboard's sweep: the same lens and the same card, aimed at a whole
    // step rather than a pointer's x, and let go when focus leaves.
    aimRef.current = (ix) => {
      if (ix === null) {
        sw.want = 0
      } else {
        measure(el.getBoundingClientRect())
        sw.target = Math.max(0, Math.min(barRefs.current.length - 1, ix))
        sw.want = 1
        setCsIx(ix)
      }
      wake()
    }
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerenter', move)
    el.addEventListener('pointerleave', leave)
    el.addEventListener('pointerdown', down)
    el.addEventListener('pointerup', up)
    el.addEventListener('pointercancel', up)
    return () => {
      aimRef.current = null
      el.removeEventListener('pointermove', move)
      el.removeEventListener('pointerenter', move)
      el.removeEventListener('pointerleave', leave)
      el.removeEventListener('pointerdown', down)
      el.removeEventListener('pointerup', up)
      el.removeEventListener('pointercancel', up)
      if (sw.raf) cancelAnimationFrame(sw.raf)
      sw.raf = 0
      sw.strength = 0
      sw.want = 0
      dragRef.current = null
    }
  }, [railEl, pitch, total])

  if (total < 2) return null

  // Refs are keyed by index; a prune shrinks the list and would otherwise leave
  // stale elements past the end for the frame loop to keep writing to.
  barRefs.current.length = ledger.steps.length

  const stepUsd = (st: Step): string =>
    st.kind === 'charged' ? fmtUsd(st.usd) : st.kind === 'free' ? 'free' : '—'

  const hovered = csIx !== null ? ledger.steps[Math.min(csIx, ledger.steps.length - 1)] : null
  const hovIx = hovered && csIx !== null ? Math.min(csIx, ledger.steps.length - 1) : null
  /** The keyboard's step, held inside the list it indexes: an undo or a prune
   *  can shorten the rail under a focused list. */
  const keyIx = keyAt === null ? null : Math.min(keyAt, total - 1)
  const optionId = (i: number) => `${listId}-step-${i}`
  const pct = (i: number) => `${(i / total) * 100}%`
  // A tick is a stroke, not a block: under half its step's width, and never
  // past 3px however few the steps, nor under 1px however many.
  const tickW = Math.max(1, Math.min(3, pitch * 0.45))

  /* The listbox's three handlers. Focus and blur are the list's OWN — the card's
     presses sit inside it, and moving onto one is not leaving the rail. A
     press never focuses the list (see `onMouseDown` below), so focus here is the
     keyboard's; the `:focus-visible` test keeps a programmatic focus — a sheet
     handing focus back — from raising a card nobody asked for. */
  const onFocus = (e: ReactFocusEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return
    const ix = Math.max(0, Math.min(total - 1, at - 1))
    setKeyAt(ix)
    if (e.currentTarget.matches(':focus-visible')) aimRef.current?.(ix)
  }
  const onBlur = (e: ReactFocusEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
    setKeyAt(null)
    aimRef.current?.(null)
  }
  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const act = railKey(e, keyIx ?? Math.max(0, at - 1), total)
    if (!act) return
    // Stopped here, so the window's ladder never sees a key the rail answered:
    // the coach walks its cues on the arrows and takes one on Enter, and the
    // spatial view orbits on them.
    e.preventDefault()
    e.stopPropagation()
    if (act.kind === 'move') {
      setKeyAt(act.to)
      aimRef.current?.(act.to)
    } else if (act.kind === 'seek') {
      seek(act.at)
      setSaid(seekSaid(act.at, total))
    } else if (!e.repeat) {
      // Auto-repeat is not a second press: a held Shift+B would fork the fork.
      branchFromStep(act.at)
    }
  }

  return (
    <div className="steprail">
      <div
        ref={setRailEl}
        className="steprail-col"
        role="listbox"
        tabIndex={0}
        aria-label="The exploration, step by step — tick height is what each step drew"
        aria-activedescendant={keyIx !== null ? optionId(keyIx) : undefined}
        aria-describedby={`${listId}-keys`}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKey}
        // A press seeks (the pointer listeners above), and leaves focus where a
        // press on a bare part of the field leaves it: on the page. Focusing
        // the list instead would hand it the verb letters (a listbox keeps its
        // own, D161), and a reader who pressed a tick and then pressed C would
        // get nothing. A list the keyboard already holds keeps its focus.
        onMouseDown={(e) => {
          if (document.activeElement === e.currentTarget) return
          e.preventDefault()
          if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
        }}
      >
        {/* A hairline where each pass after the first begins. */}
        {starts.map((i) => (
          <div key={i} className="steprail-pass" style={{ left: pct(i) }} aria-hidden />
        ))}
        {ledger.steps.map((st, i) => {
          const n = session.nodes[st.nodeId]
          const ch = n ? channelOf(n) : 'weak'
          // What the step drew, as height above the ticks' baseline: a step
          // that called no model is a stub, the dearest stands four times that.
          const rest = st.usd === 0 ? 5 : Math.round(6 + (st.usd / (ledger.maxStep || 1)) * 8)
          const current = i === at - 1
          const reached = i <= at - 1
          const tint = reached
            ? `var(--sem-${ch})`
            : 'color-mix(in srgb, var(--text-2) 85%, transparent)'
          const base = current ? 1 : reached ? 0.55 : 0.2
          return (
            <div
              key={st.nodeId}
              id={optionId(i)}
              className="steprail-row"
              // The step the replay stands on is the selected one: what Enter
              // chose, or the last step while the graph is live.
              role="option"
              aria-selected={current}
              aria-label={stepName({
                ix: i,
                total,
                verb: originWord(st.origin, n?.parentId === null),
                label: n?.label ?? null,
                channel: CHANNEL_WORD[ch],
                drew:
                  st.kind === 'charged'
                    ? `drew ${fmtUsd(st.usd)}`
                    : st.kind === 'free'
                      ? 'free'
                      : 'no figure',
                pruned: STATUS_WORD.pruned,
              })}
              data-key={keyIx === i || undefined}
              style={{ left: pct(i), width: pct(1) }}
              title={`${n?.label ?? st.nodeId} — ${stepUsd(st)}`}
            >
              <div
                ref={(el) => {
                  barRefs.current[i] = el
                }}
                className="steprail-tick"
                data-rest={rest}
                data-base={base}
                style={
                  {
                    '--rest': rest,
                    '--base': base,
                    width: tickW,
                    background: tint,
                  } as CSSProperties
                }
              />
            </div>
          )
        })}

        {capIx >= 0 && (
          <div className="steprail-cap" style={{ left: pct(capIx) }} aria-hidden>
            <i />
            <span>CAP</span>
          </div>
        )}

        {/* Where the replay stands: on the step it has reached, its knob on the
            footer's top edge. */}
        <div
          className="steprail-head"
          style={{ left: `${((Math.max(1, at) - 0.5) / total) * 100}%` }}
          aria-hidden
        />

        {/* The preview card. Content is per-row React; position and opacity are
            the frame loop's. It never intercepts the sweep.

            Hidden from the accessibility tree, with all four presses out of
            sequential focus (tabIndex -1 below): the card is raised AND AIMED
            by the pointer sweep, so a Tab that landed here landed on chrome
            the sweep never raised — invisible mid-fade, pointed at whichever
            row was swept last. Each keeps a keyboard route that does not pass
            through it: the reading, through doors that already exist — the
            palette names the node (reveal, select, the rail), and the strip's
            own focusable press opens it at full depth; Focus and Prune, through
            the thread's foot once the node is selected; and Fork, through the
            list itself, whose Shift+B is this press on the highlighted step.
            The keyboard raises the card too, as a picture of that step, with
            the two keys printed on it. */}
        <div ref={cardRef} className="steprail-card panel" style={{ opacity: 0 }} aria-hidden>
          {hovered && hovIx !== null && (
            <CardBody
              step={hovered}
              ix={hovIx}
              total={total}
              node={session.nodes[hovered.nodeId] ?? null}
              cum={ledger.spentAt(hovIx)}
              cap={cap}
              keyed={keyIx !== null && keyIx === hovIx}
              byAutopilot={autoNodes.has(hovered.nodeId)}
              onSeek={() => seek(hovIx)}
            />
          )}
        </div>
      </div>
      {/* Outside the list, whose children may only be steps: how to work it,
          read when it takes focus, and where Enter left the replay. */}
      <span id={`${listId}-keys`} className="sr-only">
        Left and right arrows move between steps; Home and End go to the first and the last. Enter
        replays the graph to the step. Shift and B branches a new exploration from it.
      </span>
      <span className="sr-only" aria-live="polite">
        {said}
      </span>
      {runLive && (
        <div className="steprail-live" title="A run is going — new steps land here as they arrive">
          <i />
        </div>
      )}
    </div>
  )
}

function CardBody({
  step,
  ix,
  total,
  node,
  cum,
  cap,
  keyed = false,
  byAutopilot,
  onSeek,
}: {
  step: Step
  ix: number
  total: number
  node: ReasonNode | null
  cum: number
  cap: number | null
  /** Raised by the keyboard rather than a sweep: the card then says which keys
   *  reach what it offers, since its own presses are the pointer's. */
  keyed?: boolean
  byAutopilot: boolean
  /** Replay to this step — what a click on its tick does. */
  onSeek: () => void
}) {
  const busy = useStore((s) => s.busy)
  const focusId = useStore((s) => s.focusId)
  // The tick's own hue, so the dot and the bar it describes cannot disagree.
  const ch = node ? channelOf(node) : 'weak'
  const root = node?.parentId === null
  const over = cap !== null && cum > cap
  const focused = node !== null && focusId === node.id
  // A step that called no model — the demo's, or your own claim — drew nothing,
  // and says so as a figure, the same shape as the running figure beside it.
  const usd = step.kind === 'charged' || step.kind === 'free' ? fmtUsd(step.usd) : '—'
  const usdWhy =
    step.kind === 'charged' && step.splitAcross > 1
      ? `One pass produced these together, across ${step.splitAcross} nodes${step.angles ? ` and ${step.angles} angles` : ''}; its draw is split evenly across them`
      : step.kind === 'unrecorded'
        ? 'This pass predates per-step records. Its draw is in the ledger, just not on a step.'
        : step.kind === 'unpriced'
          ? 'No list rate for the model that ran this pass, so there is no figure to lean high.'
          : undefined
  return (
    <>
      {/* What kind of step, in the field card's words, and who made it. */}
      <div className="steprail-card-head">
        <i style={{ background: `var(--sem-${ch})` }} />
        <span className="steprail-kind">
          {node ? kindLabelOf(node) : CHANNEL_WORD[ch]} · {originWord(step.origin, root)}
        </span>
        <span className="steprail-who">{authorOf(step.origin, root, byAutopilot)}</span>
      </div>
      {/* The title and the one-line claim are one press: the thread's strip is
          where the whole claim lives — with every act against it. A step since
          pruned keeps the plain div: there is nothing left to open. */}
      {node ? (
        <button
          className="steprail-open"
          tabIndex={-1}
          title={`Open ${node.label} at full depth in the thread — the whole claim, and every act against it`}
          onClick={() => takeInspect(node.id)}
        >
          <span className="steprail-card-title">{node.label}</span>
          {node.claim && <span className="steprail-card-desc">{node.claim}</span>}
        </button>
      ) : (
        <div className="steprail-card-title">{STATUS_WORD.pruned}</div>
      )}
      <div className="steprail-card-meta">
        Step {ix + 1} of {total} · <span title={usdWhy}>{usd}</span> ·{' '}
        <span data-over={over || undefined}>
          {cap !== null ? `${fmtUsd(cum)} of ${fmtUsd(cap)} so far` : `${fmtUsd(cum)} so far`}
        </span>
      </div>
      <div className="steprail-card-acts">
        <button
          className="steprail-act"
          data-lead=""
          tabIndex={-1}
          aria-pressed={focused}
          disabled={!node}
          title="Replay to this step and dim everything outside its lineage and children"
          onClick={() => {
            if (!node) return
            const st = useStore.getState()
            if (focused) return st.setFocus(null)
            onSeek()
            st.select(node.id)
            st.setFocus(node.id)
          }}
        >
          {focused ? 'Unfocus' : 'Focus'}
        </button>
        <button
          className="steprail-act"
          tabIndex={-1}
          disabled={busy || !node || root}
          // The foot's own gesture, `prunePress` — the mark, the count, and the
          // undo checked against the entry it belongs to. One writer.
          title={
            !node
              ? 'Already pruned'
              : root
                ? 'The seed cannot be pruned'
                : busy
                  ? 'Not while a pass is in flight — prune when it lands'
                  : 'Remove this branch — undoable'
          }
          onClick={() => node && prunePress(node.id)}
        >
          Prune
        </button>
        <button
          className="steprail-act"
          tabIndex={-1}
          title={`Fork the exploration at this step — a new document carrying ${ix + 1} node${ix === 0 ? '' : 's'}; this one stays as it is`}
          onClick={() => branchFromStep(ix)}
        >
          Fork
        </button>
      </div>
      {keyed && (
        <div className="steprail-keys">
          <span className="kbd">↵</span> replay to here <span className="kbd">⇧B</span> branch from
          here
        </div>
      )}
    </>
  )
}

/**
 * Fork the exploration at a step — the card's press and the rail's Shift+B, one
 * door, so the lock, the sentence and the toast cannot come to differ. Every
 * branch goes through `forkAtStep`, which carries stored checks and model stamps
 * across by reference and re-decides neither (invariants 2 and 3). A fork calls
 * no model, so it refuses under the lock rather than waiting on the Up next line:
 * there is no pass to defer, and it would switch the canvas out from under one.
 */
function branchFromStep(ix: number): void {
  const st = useStore.getState()
  if (st.busy || (st.run !== null && st.run.phase !== 'ended')) {
    st.toast('One thought at a time — let the current branch finish.', 'warn')
    return
  }
  if (st.forkAtStep(ix)) {
    st.toast(`Branched at step ${ix + 1} — ${ix + 1} node${ix === 0 ? '' : 's'} carried.`, 'good')
  }
}

/* ── The transport ────────────────────────────────────────────────────── */

/** The replay transport, the footer's left end (D369): play, skip and the
 *  speed in one pill (D372), then where the replay stands, the clock and the
 *  spend. It owns the replay's clock, so it must stay mounted while a replay
 *  plays — the footer stands whenever one does. */
export function LedgerDock() {
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const playhead = useStore((s) => s.playhead)
  const playing = useStore((s) => s.playing)
  const runLive = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  const tempo = useStore((s) => s.tempo)
  const runCeiling = useStore((s) => s.runCeiling)
  const setPlayhead = useStore((s) => s.setPlayhead)
  const setPlaying = useStore((s) => s.setPlaying)
  const [speed, setSpeed] = useState<1 | 2 | 4>(1)
  // A copy opened from a link and still read first replays inside its bound
  // (`COPY_REPLAY_MS`), whichever door started it — its line's press, this play
  // button, the palette's row or the space bar — so the clock here and the
  // "(N s)" on the line are one number.
  const copy = useReadFirst()

  const ledger = useMemo(() => stepLedger(session), [session, rev])
  const live = useMemo(() => estimateSpendUsd(session.spend), [session, rev])

  const total = session.order.length
  const at = playhead ?? total
  const scrubbed = playhead !== null
  const cap = parseCap(runCeiling)
  const pace = replayPace(total, tempo, speed, copy ? COPY_REPLAY_MS : null)

  // Replay advances at the live arrival beat — one clock for the whole tool —
  // times the speed chosen here, or at a copy's bounded pace. Reaching the end
  // returns to live and says so.
  useEffect(() => {
    if (!playing) return
    const t = window.setInterval(() => {
      const s = useStore.getState()
      const n = s.session.order.length
      const cur = s.playhead ?? n
      if (cur >= n) {
        s.setPlaying(false)
        s.setPlayhead(null)
        s.toast('Replay complete — back to live.', 'good')
        return
      }
      s.setPlayhead(Math.min(n, cur + pace.stride))
    }, pace.tickMs)
    return () => window.clearInterval(t)
  }, [playing, pace.tickMs, pace.stride])

  if (total < 2) return null

  const beat = pace.stepMs
  const spentHere = ledger.spentAt(at - 1)
  const shown = scrubbed ? spentHere : live.usd
  const over = cap !== null && shown > cap
  const stepNode = session.nodes[session.order[Math.max(0, at - 1)] ?? '']
  const landedBy = originWord(stepNode?.origin ?? 'seed', stepNode?.parentId === null)

  // What the first design left out stays out (D369): the chapter word is the
  // step's title (the rail's card names the move too), and the pace of growth
  // is Settings', which has carried the same select all along.
  return (
    <div className="ledgerdock" aria-label="Replay transport and spend meter">
      <div className="ledgerdock-pill">
        <button
          className="ledgerdock-play"
          disabled={runLive}
          onClick={() => {
            if (playing) {
              setPlaying(false)
            } else {
              if (playhead === null || playhead >= total) setPlayhead(1)
              useStore.getState().setFocus(null)
              setPlaying(true)
            }
          }}
          aria-label={playing ? 'Pause replay' : 'Replay how the graph grew'}
          title={
            runLive
              ? 'Replay is off while a run is going — the graph is still growing'
              : playing
                ? 'Pause'
                : 'Replay growth'
          }
        >
          {/* Filled, as the design draws it. The pause bars are strokes, so they
              take a heavier one to stand at the triangle's weight. */}
          {playing ? (
            <Icon name="pause" size={11} strokeWidth={2.4} />
          ) : (
            <Icon name="play" size={9} fill="currentColor" />
          )}
        </button>
        {/* Skip the replay: the one-press way back to live (D156, D158), as a
            player's skip-to-end beside its play rather than the word "Now" at
            the row's end (D372). Always in the pill, and stood down while the
            graph is live — there is nothing to skip — so its arrival never
            moves the scrubber beside it: a press on a tick is what scrubs, and
            a control appearing under that press once moved every tick 60px
            from the pointer that had just landed on one. */}
        <button
          className="ledgerdock-skip"
          disabled={!scrubbed}
          onClick={() => {
            setPlaying(false)
            setPlayhead(null)
          }}
          aria-label="Skip the replay"
          title={
            scrubbed
              ? 'Skip the replay — back to the whole graph, live'
              : 'Nothing to skip — the graph is live'
          }
        >
          <Icon name="forward" size={11} fill="currentColor" />
        </button>
        {/* A select, not the click-cycle it was. Cycling 1×→2×→4× on click had
            no keyboard route, no menu and no name past a `title`. It wears no
            box — the design draws the speed as a bare "1×" beside play — but it
            is still the native select, so the keyboard and the menu stay. */}
        <label className="ledgerdock-speed" title="Replay speed">
          <span className="sr-only">Replay speed</span>
          <select value={speed} onChange={(e) => setSpeed(Number(e.target.value) as 1 | 2 | 4)}>
            <option value={1}>1×</option>
            <option value={2}>2×</option>
            <option value={4}>4×</option>
          </select>
        </label>
      </div>

      {/* "Step 12/28", at the owner's word: the step at full ink, the count a
          tier down. Sized by its widest reading, which stands unseen in the
          same cell: the label grows a digit as the playhead passes 9 and 99,
          and every piece right of it would shuffle along with it, mid-replay. */}
      <span
        className="ledgerdock-step"
        title={`Step ${at} of ${total}, the step under the playhead · ${landedBy}`}
      >
        <span>
          <b>Step {at}</b>/{total}
        </span>
        <span className="ledgerdock-step-widest" aria-hidden>
          <b>Step {total}</b>/{total}
        </span>
      </span>

      {/* One time, where the replay stands — not that time out of the whole,
          at the owner's word. The whole is the clock's title, and holds its
          width unseen, for the step label's reason. */}
      <span
        className="ledgerdock-clock"
        title={`${fmtClock((at - 1) * beat)} of ${fmtClock((total - 1) * beat)}`}
      >
        <span>{fmtClock((at - 1) * beat)}</span>
        <span className="ledgerdock-step-widest" aria-hidden>
          {fmtClock((total - 1) * beat)}
        </span>
      </span>

      <span
        className="ledgerdock-meter"
        title={
          over
            ? 'Past the cap for this run. Crossing it blocks nothing — it recolors.'
            : scrubbed
              ? 'Estimated spend by this point of the replay — it leans high on purpose'
              : cap !== null
                ? 'Estimated spend so far, against your cap — it leans high on purpose'
                : 'Estimated spend so far. Set a cap in the composer to draw the line here.'
        }
      >
        {/* Drawn only against a cap (D335). With none — the default, since the
            ceiling is not persisted — the bar was an empty track beside the
            figure that never filled however much was drawn: progress toward a
            limit nobody had set. The figure alone says what there is to say. */}
        {cap !== null && (
          <span className="ledgerdock-track">
            <i
              style={{
                width: `${Math.min(100, (shown / cap) * 100)}%`,
                background: over ? 'var(--sem-tension)' : 'var(--sem-insight)',
              }}
            />
          </span>
        )}
        {/* "so far" in both states: live it is the session's draw, scrubbed it is
            the draw by the step under the playhead, which is what "· by here"
            used to say beside it. */}
        {/* Held at the widest the figure gets — the session's draw, which a
            scrubbed step can only fall short of — for the step label's reason:
            a figure losing a digit mid-scrub moved the ticks under the press. */}
        <span className="ledgerdock-amount" data-over={over || undefined}>
          <span>{cap !== null ? `${fmtUsd(shown)} of ${fmtUsd(cap)}` : fmtUsd(shown)}</span>
          <span className="ledgerdock-step-widest" aria-hidden>
            {cap !== null
              ? `${fmtUsd(Math.max(shown, live.usd))} of ${fmtUsd(cap)}`
              : fmtUsd(Math.max(shown, live.usd))}
          </span>
        </span>{' '}
        so far
        {(ledger.unrecorded > 0 || ledger.unpriced > 0) && scrubbed && (
          <span
            className="ledgerdock-gap"
            title={`${ledger.unrecorded + ledger.unpriced} pass${
              ledger.unrecorded + ledger.unpriced === 1 ? '' : 'es'
            } here ${
              ledger.unrecorded + ledger.unpriced === 1 ? 'has' : 'have'
            } no per-step figure — the ledger still carries their draw in aggregate.`}
          >
            *
          </span>
        )}
      </span>
    </div>
  )
}

/* ── The footer ───────────────────────────────────────────────────────── */

/**
 * The graph's footer (D369): one strip along the field's bottom edge, holding
 * the replay transport at its left, the scrubber across its middle, and the
 * view's switches at its right — the depth rings, the dimming of what the
 * selected claim does not stand on or hold up, the minimap, and the zoom.
 *
 * `atRest` is whether it stands while no replay is under way. A desktop's
 * does. A phone's composer column is the whole bottom of a 390px screen, and
 * this strip at rest would be two more rows of it over a canvas the column
 * already crowds (D158), so there it rests behind Adjust and stands by itself
 * only while a replay plays or is scrubbed — and its skip is never hidden. A
 * phone reaches the replay from the palette's Replay row (the tool dock's
 * search button is its touch door). Passed from App, which knows the phone.
 */
export function GraphFooter({
  stageW,
  stageH,
  atRest,
  narrow,
}: {
  stageW: number
  stageH: number
  atRest: boolean
  narrow: boolean
}) {
  const steps = useStore((s) => s.session.order.length)
  const replaying = useStore((s) => s.playing || s.playhead !== null)
  if (steps === 0 || (!atRest && !replaying)) return null
  const timeline = steps >= 2
  return (
    <footer className="graphfoot" data-narrow={narrow || undefined} aria-label="Replay and view">
      {timeline && (
        <>
          <LedgerDock />
          <i className="graphfoot-rule" aria-hidden />
          <StepRail />
          <i className="graphfoot-rule" aria-hidden />
        </>
      )}
      <ViewToggles stageW={stageW} stageH={stageH} />
    </footer>
  )
}
